package org.example.project.enginesmartring

import androidx.compose.runtime.*
import kotlin.math.PI
import kotlin.math.ceil
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.sqrt
import org.example.project.dashboard.DashboardTheme
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.settings.LogoSettings
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private fun updateRingSlots(
    slots: List<String?>,
    targetIndex: Int,
    screenKey: String
): List<String?> {
    val normalized = List(ENGINE_SMARTRING_SLOT_COUNT) { index -> slots.getOrNull(index) }
    val mutable = normalized.toMutableList()
    val previousIndex = mutable.indexOf(screenKey)
    if (previousIndex >= 0) mutable[previousIndex] = null
    mutable[targetIndex] = screenKey
    return mutable
}

@Composable
private fun EngineSmartRingSlotPicker(
    availableScreens: List<All>,
    onSelect: (String) -> Unit
) {
    val gridSize = minOf(6, maxOf(1, ceil(sqrt(availableScreens.size.toDouble())).toInt()))

    Div({ classes(EngineSmartRingSettingsStyles.pickerPage) }) {
        Div({
            classes(EngineSmartRingSettingsStyles.list)
            style { property("grid-template-columns", "repeat($gridSize, 94px)") }
        }) {
            availableScreens.forEach { screen ->
                Div({
                    classes(EngineSmartRingSettingsStyles.pickerSlot)
                    attr("draggable", "true")
                    onDragStart { event -> event.dataTransfer?.setData("text/plain", screen.key) }
                    onClick { onSelect(screen.key) }
                }) {
                    Img(screen.icon.ifBlank { "images/step.svg" }) {
                        classes(EngineSmartRingSettingsStyles.pickerSlotIcon)
                        style { property("filter", DashboardTheme.ICON_FILTER_WHITE) }
                    }
                    Span({ classes(EngineSmartRingSettingsStyles.pickerSlotLabel) }) { Text(screen.label) }
                }
            }
        }
    }
}

@Composable
fun EngineSmartRingSettingsLayout(
    allowedScreens: List<All>,
    initialSlots: List<String?>,
    onSave: (List<String>) -> Unit
) {
    val internalTitleVisible = LocalScreenInternalTitleVisible.current
    var draftSlots by remember(initialSlots, allowedScreens) {
        mutableStateOf(
            List(ENGINE_SMARTRING_SLOT_COUNT) { index ->
                initialSlots.getOrNull(index)?.takeIf { key -> allowedScreens.any { it.key == key } }
            }
        )
    }
    var activeEmptySlotIndex by remember { mutableStateOf<Int?>(null) }
    val screensByKey = remember(allowedScreens) { allowedScreens.associateBy { it.key } }
    val availableScreens = remember(allowedScreens, draftSlots) {
        allowedScreens.filter { screen -> draftSlots.none { it == screen.key } }
    }
    val ringRadiusPx = 150
    val centerPx = 190

    Style(EngineSmartRingSettingsStyles)

    Div({ classes(EngineSmartRingSettingsStyles.screen) }) {
        Div({ classes(EngineSmartRingSettingsStyles.dialog) }) {
            if (internalTitleVisible) {
                Div({ classes(EngineSmartRingSettingsStyles.header) }) {
                    H2({ classes(EngineSmartRingSettingsStyles.title) }) { Text("Lumatica SmartRing") }
                }
            }

            Div({ classes(EngineSmartRingSettingsStyles.body) }) {
                if (activeEmptySlotIndex != null) {
                    Div({
                        classes(EngineSmartRingSettingsStyles.backAnchor)
                        onClick { activeEmptySlotIndex = null }
                    }) {
                        Img(src = "images/back.svg") {
                            classes(EngineSmartRingSettingsStyles.backIcon)
                            style { property("filter", DashboardTheme.ICON_FILTER_WHITE) }
                        }
                    }

                    Div({ classes(EngineSmartRingSettingsStyles.pickerStage) }) {
                        if (availableScreens.isEmpty()) {
                            Div({ classes(EngineSmartRingSettingsStyles.emptyHint) }) {
                                Text("Keine Screens verfügbar.")
                            }
                        } else {
                            EngineSmartRingSlotPicker(
                                availableScreens = availableScreens,
                                onSelect = { screenKey ->
                                    val targetIndex = activeEmptySlotIndex ?: return@EngineSmartRingSlotPicker
                                    if (allowedScreens.none { it.key == screenKey }) return@EngineSmartRingSlotPicker
                                    draftSlots = updateRingSlots(draftSlots, targetIndex, screenKey)
                                    activeEmptySlotIndex = null
                                }
                            )
                        }
                    }
                } else {
                    Div({ classes(EngineSmartRingSettingsStyles.ringStage) }) {
                        Div({ classes(EngineSmartRingSettingsStyles.canvas) }) {
                            draftSlots.forEachIndexed { index, screenKey ->
                                val angle = (-90.0 + (360.0 / ENGINE_SMARTRING_SLOT_COUNT) * index) * (PI / 180.0)
                                val leftPx = centerPx + cos(angle) * ringRadiusPx - 42.0
                                val topPx = centerPx + sin(angle) * ringRadiusPx - 34.0
                                val screen = screenKey?.let { screensByKey[it] }

                                Div({
                                    classes(EngineSmartRingSettingsStyles.slot)
                                    style {
                                        left(leftPx.px)
                                        top(topPx.px)
                                    }
                                    onDragOver { event -> event.preventDefault() }
                                    onDrop { event ->
                                        val draggedKey = event.dataTransfer?.getData("text/plain").orEmpty()
                                        if (draggedKey.isBlank()) return@onDrop
                                        if (allowedScreens.none { it.key == draggedKey }) return@onDrop
                                        draftSlots = updateRingSlots(draftSlots, index, draggedKey)
                                        activeEmptySlotIndex = null
                                    }
                                    onClick {
                                        if (screen != null) {
                                            draftSlots = draftSlots.toMutableList().also { it[index] = null }
                                        }
                                        activeEmptySlotIndex = index
                                    }
                                }) {
                                    if (screen != null) {
                                        Div({ classes(EngineSmartRingSettingsStyles.slotBubble) }) {
                                            Img(screen.icon.ifBlank { "images/step.svg" }) {
                                                classes(EngineSmartRingSettingsStyles.slotIcon)
                                                style { property("filter", DashboardTheme.ICON_FILTER_WHITE) }
                                            }
                                        }
                                        Span({ classes(EngineSmartRingSettingsStyles.slotLabel) }) { Text(screen.label) }
                                    } else {
                                        Div({ classes(EngineSmartRingSettingsStyles.slotBubble) }) {
                                            Span({ classes(EngineSmartRingSettingsStyles.slotEmpty) }) { Text("+") }
                                        }
                                    }
                                }
                            }
                            Div({ classes(EngineSmartRingSettingsStyles.center) }) {
                                Img(LogoSettings.smartRingSettingsLogo) {
                                    classes(EngineSmartRingSettingsStyles.centerLogo)
                                }
                            }
                        }
                    }
                }
            }

            Div({ classes(EngineSmartRingSettingsStyles.footer) }) {
                if (activeEmptySlotIndex == null) {
                    Button({
                        classes(EngineSmartRingSettingsStyles.primaryButton)
                        onClick { onSave(draftSlots.map { it.orEmpty() }) }
                    }) { Text("Speichern") }
                    Button({
                        classes(EngineSmartRingSettingsStyles.primaryButton)
                        style { marginLeft(12.px) }
                        onClick {
                            draftSlots = List(ENGINE_SMARTRING_SLOT_COUNT) { null }
                            activeEmptySlotIndex = null
                            onSave(List(ENGINE_SMARTRING_SLOT_COUNT) { "" })
                        }
                    }) { Text("Ring leeren") }
                }
            }
        }
    }
}

object EngineSmartRingSettingsStyles : StyleSheet() {
    val screen by style {
        position(Position.Relative)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.FlexStart)
        width(100.percent)
        minHeight(100.percent)
        padding(24.px, 0.px, 40.px, 0.px)
        property("box-sizing", "border-box")
    }
    val dialog by style {
        width(100.percent)
        maxWidth(100.percent)
        height(100.percent)
        minHeight(0.px)
        maxHeight(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        padding(18.px, 0.px, 24.px, 0.px)
        backgroundColor(Color.transparent)
        property("border", "0")
        property("border-radius", "0")
        property("box-shadow", "none")
        property("box-sizing", "border-box")
    }
    val header by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        position(Position.Relative)
        gap(16.px)
        marginBottom(26.px)
    }
    val title by style {
        margin(0.px)
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(26.px)
        textAlign("center")
    }
    val body by style {
        position(Position.Relative)
        flex(1)
        minHeight(0.px)
        width(100.percent)
    }
    val ringStage by style {
        width(100.percent)
        height(100.percent)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
    }
    val pickerStage by style {
        width(100.percent)
        height(100.percent)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        padding(0.px, 24.px, 0.px, 92.px)
        property("box-sizing", "border-box")
    }
    val canvas by style {
        position(Position.Relative)
        width(380.px)
        height(380.px)
        alignSelf(AlignSelf.Center)
        flexShrink(0)
        borderRadius(50.percent)
        property("border", "1px solid transparent")
        backgroundColor(Color.transparent)
    }
    val slot by style {
        position(Position.Absolute)
        width(84.px)
        minHeight(94.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        gap(6.px)
        property("box-sizing", "border-box")
        color(DashboardTheme.TEXT_LIGHT)
        cursor("pointer")
        property("text-align", "center")
    }
    val slotBubble by style {
        width(68.px)
        height(68.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        padding(8.px)
        property("box-sizing", "border-box")
        property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
        borderRadius(50.percent)
        backgroundColor(Color("rgba(255,255,255,0.05)"))
    }
    val slotIcon by style {
        width(34.px)
        height(34.px)
        display(DisplayStyle.Block)
        property("object-fit", "contain")
    }
    val slotLabel by style {
        fontSize(10.px)
        lineHeight("1.15")
        fontWeight("700")
        property("max-width", "100%")
        property("white-space", "normal")
        property("overflow", "hidden")
        property("display", "-webkit-box")
        property("-webkit-line-clamp", "2")
        property("-webkit-box-orient", "vertical")
        color(DashboardTheme.TEXT_MUTED)
    }
    val slotEmpty by style {
        fontSize(20.px)
        color(DashboardTheme.TEXT_MUTED)
    }
    val emptyHint by style {
        color(DashboardTheme.TEXT_MUTED)
        fontSize(15.px)
        fontWeight("700")
        textAlign("center")
    }
    val center by style {
        position(Position.Absolute)
        left(50.percent)
        top(50.percent)
        property("transform", "translate(-50%, -50%)")
        width(96.px)
        height(96.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        borderRadius(50.percent)
        property("border", "1px solid transparent")
        color(Color.transparent)
        fontSize(30.px)
        fontWeight("700")
        backgroundColor(Color.transparent)
    }
    val centerLogo by style {
        width(100.percent)
        height(100.percent)
        property("object-fit", "contain")
        display(DisplayStyle.Block)
    }
    val pickerPage by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        flex(1)
        width(100.percent)
        height(100.percent)
        minHeight(0.px)
    }
    val list by style {
        display(DisplayStyle.Grid)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        gap(18.px)
        overflowY("auto")
        minHeight(0.px)
        maxHeight(100.percent)
        property("width", "max-content")
    }
    val pickerSlot by style {
        width(68.px)
        height(68.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        gap(4.px)
        padding(6.px)
        property("box-sizing", "border-box")
        property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
        borderRadius(50.percent)
        backgroundColor(Color("rgba(255,255,255,0.05)"))
        color(DashboardTheme.TEXT_LIGHT)
        cursor("pointer")
        property("text-align", "center")
        flexShrink(0)
    }
    val pickerSlotIcon by style {
        width(24.px)
        height(24.px)
        display(DisplayStyle.Block)
    }
    val pickerSlotLabel by style {
        fontSize(9.px)
        lineHeight("1.1")
        property("max-width", "100%")
        property("white-space", "nowrap")
        property("overflow", "hidden")
        property("text-overflow", "ellipsis")
    }
    val footer by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        alignItems(AlignItems.Center)
        gap(10.px)
        minHeight(40.px)
        padding(0.px, 20.px, 0.px, 20.px)
    }
    val primaryButton by style {
        padding(10.px, 18.px)
        backgroundColor(Color.transparent)
        color(DashboardTheme.TEXT_LIGHT)
        property("border", "1px solid ${DashboardTheme.ACCENT}")
        borderRadius(999.px)
        cursor("pointer")
        property("box-shadow", "0 0 18px rgba(255, 0, 0, 0.18)")
    }
    val backIcon by style {
        width(28.px)
        height(28.px)
        display(DisplayStyle.Block)
    }
    val backAnchor by style {
        position(Position.Absolute)
        left(20.px)
        top(50.percent)
        property("transform", "translateY(-50%)")
        cursor("pointer")
        property("z-index", "2")
    }
}
